import React, { useState, useEffect } from 'react';
import { perizinanIptService } from '../../../services/perizinanIptService';
import { useAuth } from '../../Admin/context/AuthContext';
import {
    FaSearch, FaFilter, FaEye, FaCheckCircle, FaTimesCircle,
    FaTrash, FaFilePdf, FaImage, FaDownload, FaSpinner,
    FaExclamationTriangle, FaChevronLeft, FaChevronRight,
    FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt,
    FaFileAlt, FaClock, FaCheck, FaBan, FaList, FaInfoCircle,
    FaWhatsapp, FaPaperPlane
} from 'react-icons/fa';

const statusConfig = {
    baru: { label: 'Baru', color: 'bg-blue-100 text-blue-700', icon: FaPaperPlane },
    diproses: { label: 'Diproses', color: 'bg-yellow-100 text-yellow-700', icon: FaClock },
    disetujui: { label: 'Disetujui', color: 'bg-green-100 text-green-700', icon: FaCheckCircle },
    ditolak: { label: 'Ditolak', color: 'bg-red-100 text-red-700', icon: FaTimesCircle },
};

const ManajemenPermohonanIpt = () => {
    const { user } = useAuth();
    const hasPermission = (p) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        return user?.permissions?.includes(p) || false;
    };

    // State data
    const [submissions, setSubmissions] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filter
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Modal detail
    const [detailModal, setDetailModal] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Modal update status
    const [statusModal, setStatusModal] = useState(null);
    const [statusAction, setStatusAction] = useState('diproses');
    const [statusCatatan, setStatusCatatan] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Delete
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Notification
    const [notification, setNotification] = useState(null);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // Load data
    const loadData = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterStatus) params.status = filterStatus;

        const [listRes, statRes] = await Promise.all([
            perizinanIptService.getSubmissions(params),
            perizinanIptService.getSubmissionStatistics(),
        ]);

        if (listRes.success) {
            setSubmissions(listRes.data?.data || []);
            setTotalPages(listRes.data?.last_page || 1);
            setCurrentPage(listRes.data?.current_page || 1);
        }
        if (statRes.success) {
            setStatistics(statRes.data || {});
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        loadData(1);
    };

    // Buka detail
    const openDetail = async (id) => {
        setDetailLoading(true);
        setDetailModal({ id, loading: true });
        const result = await perizinanIptService.getSubmissionDetail(id);
        if (result.success) {
            setDetailModal(result.data);
        } else {
            showNotif(result.message, 'error');
            setDetailModal(null);
        }
        setDetailLoading(false);
    };

    // Buka modal update status
    const openStatusModal = (submission, action = 'diproses') => {
        setStatusModal(submission);
        setStatusAction(action);
        setStatusCatatan('');
    };

    // Submit update status
    const handleUpdateStatus = async () => {
        if (!statusModal) return;
        setSubmitting(true);
        const result = await perizinanIptService.updateSubmissionStatus(statusModal.id, {
            status: statusAction,
            catatan_admin: statusCatatan,
        });
        if (result.success) {
            showNotif(result.message || 'Status berhasil diubah');
            setStatusModal(null);
            loadData(currentPage);
            // Refresh detail jika sedang dibuka
            if (detailModal?.id === statusModal.id) {
                openDetail(statusModal.id);
            }
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    // Hapus
    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await perizinanIptService.deleteSubmission(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData(currentPage);
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    // Format helpers
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const getFileName = (path) => path?.split('/').pop() || 'file';

    const getFileIcon = (path) => {
        const ext = path?.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return <FaFilePdf className="text-red-500" />;
        if (['jpg', 'jpeg', 'png'].includes(ext)) return <FaImage className="text-blue-500" />;
        return <FaFileAlt className="text-gray-500" />;
    };

    const StatusBadge = ({ status }) => {
        const config = statusConfig[status] || statusConfig.baru;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="mr-1" size={10} /> {config.label}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#0F4C3A]">Permohonan Perizinan IPT</h2>
                <p className="text-gray-600">Review dan verifikasi permohonan perizinan tanah</p>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {[
                    { label: 'Total', value: statistics.total, color: 'text-[#0F4C3A]' },
                    { label: 'Baru', value: statistics.baru, color: 'text-blue-600' },
                    { label: 'Diproses', value: statistics.diproses, color: 'text-yellow-600' },
                    { label: 'Disetujui', value: statistics.disetujui, color: 'text-green-600' },
                    { label: 'Ditolak', value: statistics.ditolak, color: 'text-red-600' },
                    { label: 'Hari Ini', value: statistics.hari_ini, color: 'text-purple-600' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cari nama, NIK, atau nomor registrasi..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    >
                        <option value="">Semua Status</option>
                        <option value="baru">Baru</option>
                        <option value="diproses">Diproses</option>
                        <option value="disetujui">Disetujui</option>
                        <option value="ditolak">Ditolak</option>
                    </select>
                    <button type="submit" className="btn-primary flex items-center justify-center">
                        <FaFilter className="mr-2" /> Filter
                    </button>
                </form>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4 font-medium">No. Registrasi</th>
                                <th className="p-4 font-medium">Pemohon</th>
                                <th className="p-4 font-medium">Lokasi Tanah</th>
                                <th className="p-4 font-medium">Luas</th>
                                <th className="p-4 font-medium">Tanggal</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center">
                                        <FaSpinner className="animate-spin text-2xl text-[#2E7D32] mx-auto" />
                                    </td>
                                </tr>
                            ) : submissions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-gray-500">
                                        Belum ada permohonan
                                    </td>
                                </tr>
                            ) : (
                                submissions.map((sub) => (
                                    <tr key={sub.id} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <p className="font-mono text-sm font-semibold text-[#0F4C3A]">
                                                {sub.nomor_registrasi}
                                            </p>
                                        </td>
                                        <td className="p-4">
                                            <p className="font-medium text-gray-700">{sub.nama_lengkap}</p>
                                            <p className="text-xs text-gray-500">NIK: {sub.nik}</p>
                                            <p className="text-xs text-gray-500">{sub.nomor_telepon}</p>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            <span className="line-clamp-2">{sub.lokasi_tanah}</span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {sub.luas_tanah} m²
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">
                                            {formatDate(sub.created_at)}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge status={sub.status} />
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-1">
                                                {/* Detail */}
                                                <button
                                                    onClick={() => openDetail(sub.id)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                    title="Detail"
                                                >
                                                    <FaEye size={14} />
                                                </button>

                                                {/* Update status */}
                                                {hasPermission('perizinan-ipt.update') && (
                                                    <>
                                                        {sub.status !== 'diproses' && sub.status !== 'disetujui' && (
                                                            <button
                                                                onClick={() => openStatusModal(sub, 'diproses')}
                                                                className="p-2 text-yellow-500 hover:bg-yellow-50 rounded-lg"
                                                                title="Proses"
                                                            >
                                                                <FaClock size={14} />
                                                            </button>
                                                        )}
                                                        {sub.status !== 'disetujui' && (
                                                            <button
                                                                onClick={() => openStatusModal(sub, 'disetujui')}
                                                                className="p-2 text-green-500 hover:bg-green-50 rounded-lg"
                                                                title="Setujui"
                                                            >
                                                                <FaCheck size={14} />
                                                            </button>
                                                        )}
                                                        {sub.status !== 'ditolak' && (
                                                            <button
                                                                onClick={() => openStatusModal(sub, 'ditolak')}
                                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                                title="Tolak"
                                                            >
                                                                <FaBan size={14} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}

                                                {/* Hapus */}
                                                {hasPermission('perizinan-ipt.delete') && (
                                                    <button
                                                        onClick={() => setDeleteConfirm(sub)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                        title="Hapus"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">
                            Halaman {currentPage} dari {totalPages}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => loadData(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronLeft size={12} />
                            </button>
                            <button
                                onClick={() => loadData(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronRight size={12} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ============ MODAL DETAIL ============ */}
            {detailModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <div>
                                <h3 className="text-xl font-bold text-[#0F4C3A]">Detail Permohonan</h3>
                                <p className="text-sm text-gray-500 font-mono mt-1">
                                    {detailModal.nomor_registrasi}
                                </p>
                            </div>
                            <button
                                onClick={() => setDetailModal(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimesCircle size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-5">
                            {detailLoading ? (
                                <div className="text-center py-8">
                                    <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto" />
                                </div>
                            ) : (
                                <>
                                    {/* Status */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Status Permohonan</p>
                                            <StatusBadge status={detailModal.status} />
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 mb-1">Tanggal Pengajuan</p>
                                            <p className="text-sm font-semibold">{formatDate(detailModal.created_at)}</p>
                                        </div>
                                    </div>

                                    {/* Data Pemohon */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                            <FaUser className="mr-2 text-[#2E7D32]" /> Data Pemohon
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Nama Lengkap</p>
                                                <p className="text-sm font-semibold">{detailModal.nama_lengkap}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">NIK</p>
                                                <p className="text-sm font-semibold font-mono">{detailModal.nik}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Telepon</p>
                                                <p className="text-sm font-semibold">{detailModal.nomor_telepon}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Email</p>
                                                <p className="text-sm font-semibold">{detailModal.email || '-'}</p>
                                            </div>
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Alamat</p>
                                                <p className="text-sm">{detailModal.alamat_lengkap}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Permohonan */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                            <FaFileAlt className="mr-2 text-[#2E7D32]" /> Data Permohonan
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Lokasi Tanah</p>
                                                <p className="text-sm font-semibold">{detailModal.lokasi_tanah}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Luas Tanah</p>
                                                <p className="text-sm font-semibold">{detailModal.luas_tanah} m²</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Jumlah File</p>
                                                <p className="text-sm font-semibold">
                                                    {Array.isArray(detailModal.file_paths) ? detailModal.file_paths.length : 0} file
                                                </p>
                                            </div>
                                            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500">Tujuan Penggunaan</p>
                                                <p className="text-sm whitespace-pre-wrap">{detailModal.tujuan_penggunaan}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* File Pendukung */}
                                    {Array.isArray(detailModal.file_paths) && detailModal.file_paths.length > 0 && (
                                        <div>
                                            <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                                <FaFilePdf className="mr-2 text-[#2E7D32]" /> Dokumen Pendukung
                                            </h4>
                                            <div className="space-y-2">
                                                {detailModal.file_paths.map((path, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            {getFileIcon(path)}
                                                            <p className="text-sm truncate">{getFileName(path)}</p>
                                                        </div>
                                                        <a
                                                            href={perizinanIptService.getSubmissionFileUrl(detailModal.id, index)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1.5 text-[#2E7D32] hover:bg-green-50 rounded"
                                                            title="Download"
                                                        >
                                                            <FaDownload size={14} />
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Catatan Admin */}
                                    {detailModal.catatan_admin && (
                                        <div>
                                            <h4 className="font-bold text-[#0F4C3A] mb-3 flex items-center">
                                                <FaInfoCircle className="mr-2 text-[#2E7D32]" /> Catatan Admin
                                            </h4>
                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                <p className="text-sm whitespace-pre-wrap">{detailModal.catatan_admin}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Metadata */}
                                    <div>
                                        <h4 className="font-bold text-[#0F4C3A] mb-3">Informasi Sistem</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-gray-500">IP Address</p>
                                                <p className="font-mono">{detailModal.ip_address || '-'}</p>
                                            </div>
                                            <div className="p-3 bg-gray-50 rounded-lg">
                                                <p className="text-gray-500">Update Terakhir</p>
                                                <p>{formatDate(detailModal.updated_at)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action Footer */}
                        {!detailLoading && detailModal && hasPermission('perizinan-ipt.update') && (
                            <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'diproses')}
                                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-600"
                                    >
                                        <FaClock size={12} /> Proses
                                    </button>
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'disetujui')}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-green-700"
                                    >
                                        <FaCheck size={12} /> Setujui
                                    </button>
                                    <button
                                        onClick={() => openStatusModal(detailModal, 'ditolak')}
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-red-600"
                                    >
                                        <FaBan size={12} /> Tolak
                                    </button>
                                </div>
                                <button
                                    onClick={() => setDetailModal(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
                                >
                                    Tutup
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ============ MODAL UPDATE STATUS ============ */}
            {statusModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold text-[#0F4C3A] mb-4">
                            Update Status Permohonan
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            No. Registrasi: <strong className="font-mono">{statusModal.nomor_registrasi}</strong>
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Status Baru</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(statusConfig).map(([key, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setStatusAction(key)}
                                                className={`py-3 px-4 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${statusAction === key
                                                        ? 'border-[#2E7D32] bg-green-50 text-[#2E7D32]'
                                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                                    }`}
                                            >
                                                <Icon size={14} />
                                                {config.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Catatan Admin {statusAction === 'ditolak' && <span className="text-red-500">*</span>}
                                </label>
                                <textarea
                                    value={statusCatatan}
                                    onChange={(e) => setStatusCatatan(e.target.value)}
                                    rows="3"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                    placeholder={
                                        statusAction === 'ditolak'
                                            ? 'Jelaskan alasan penolakan...'
                                            : 'Catatan untuk pemohon (opsional)'
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setStatusModal(null)}
                                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleUpdateStatus}
                                disabled={submitting || (statusAction === 'ditolak' && !statusCatatan.trim())}
                                className="px-5 py-2.5 bg-[#2E7D32] text-white rounded-lg font-semibold hover:bg-[#1B5E20] disabled:opacity-50 flex items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <FaSpinner className="animate-spin" /> Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <FaCheckCircle /> Simpan
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ DELETE CONFIRM ============ */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Permohonan?</h3>
                        <p className="text-gray-600 mb-6">
                            Hapus permohonan <strong>{deleteConfirm.nomor_registrasi}</strong> milik <strong>{deleteConfirm.nama_lengkap}</strong>? Tindakan ini tidak dapat dibatalkan.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenPermohonanIpt;